import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { reorderedIds } from '#shared/ordering'
import { api, HttpError, type TagWithUsage } from '../api/client'
import { SortableRow } from './SortableRow'

interface Props {
  /** Every tag, archived ones included, each with its entry count. */
  tags: TagWithUsage[]
  /** False for members: the same list, read-only. */
  canManage: boolean
  onChanged: () => void | Promise<void>
}

/**
 * The tag vocabulary, managed in one place.
 *
 * Tags are global — they belong to no project — so this is a card of its own
 * rather than something nested inside a project row, which is where the
 * features they replaced used to live. Otherwise it is the project list:
 * same rows, same drag handle, same rename and archive, because it is the
 * same job and a second set of conventions would only be something else to
 * learn.
 *
 * The order is the point of the dragging. The picker on the log form reads
 * this list, and alphabetical put whatever the team reaches for most
 * wherever the alphabet happened to leave it.
 *
 * There is no delete here. Archiving takes a tag out of the picker and keeps
 * its name on the time already logged under it, which is almost always what
 * is actually wanted; deleting would leave that history unlabelled.
 */
export function TagsCard({ tags, canManage, onChanged }: Props) {
  const [name, setName] = useState('')
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  // Mirrors the server order while a save is in flight, so the row stays
  // where it was dropped instead of snapping back and then jumping forward.
  const [pending, setPending] = useState<TagWithUsage[] | null>(null)

  const addRef = useRef<HTMLInputElement>(null)
  const editRef = useRef<HTMLInputElement>(null)

  const source = pending ?? tags
  const active = source.filter((t) => !t.archived)
  const archived = source.filter((t) => t.archived)

  const sensors = useSensors(
    // A few pixels of movement before a drag starts, so clicking a button
    // inside the row is never mistaken for the beginning of a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Opening either form should put the cursor where typing starts.
  useEffect(() => {
    if (adding) addRef.current?.focus()
  }, [adding])
  useEffect(() => {
    if (editingId) editRef.current?.focus()
  }, [editingId])

  function fail(err: unknown, fallback: string) {
    setError(err instanceof HttpError ? err.message : fallback)
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active: dragged, over } = event
    if (!over || dragged.id === over.id) return

    const ids = reorderedIds(active, archived, String(dragged.id), String(over.id))
    const byId = new Map(source.map((t) => [t.id, t]))

    // Show the new order immediately; the request confirms or undoes it.
    setPending(ids.map((id) => byId.get(id)!).filter(Boolean))

    try {
      await api.reorderTags(ids)
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not save the new order.')
    } finally {
      setPending(null)
    }
  }

  async function handleAdd(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return

    setSaving(true)
    try {
      await api.createTag({ name })
      setName('')
      setAdding(false)
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not add that tag.')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveEdit(id: string) {
    if (!draft.trim()) return

    setSaving(true)
    try {
      // A rename reaches every entry already carrying it: the link is by id,
      // so history follows the new name rather than keeping the old one.
      await api.updateTag(id, { name: draft })
      setEditingId(null)
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not save that tag.')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive(tag: TagWithUsage) {
    const used = tag.entries
      ? ` It is on ${tag.entries} ${tag.entries === 1 ? 'entry' : 'entries'}, which keep it.`
      : ''
    if (!window.confirm(`Archive ${tag.name}? Nobody can add it to new time.${used}`)) return

    try {
      await api.updateTag(tag.id, { archived: true })
      setEditingId(null)
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not archive that tag.')
    }
  }

  async function handleRestore(tag: TagWithUsage) {
    try {
      await api.updateTag(tag.id, { archived: false })
      setError(null)
      await onChanged()
    } catch (err) {
      fail(err, 'Could not restore that tag.')
    }
  }

  /** How much history a tag carries, under its name. */
  function uses(tag: TagWithUsage) {
    return (
      <span className="row-desc">
        {tag.entries === 0
          ? 'Not used yet'
          : `On ${tag.entries} ${tag.entries === 1 ? 'entry' : 'entries'}`}
      </span>
    )
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Tags</h2>
        <span className="count">{active.length} active</span>
      </div>

      <p className="muted card-subtitle">
        {canManage
          ? 'Shared by every project. Drag a tag to reorder it — this is the order everyone sees when logging time.'
          : 'Shared by every project. Pick as many as apply when logging time, or none.'}
      </p>

      {error && <p className="error">{error}</p>}

      {active.length === 0 && <p className="empty">No tags yet.</p>}

      {active.length > 0 &&
        (!canManage ? (
          <ul className="rows">
            {active.map((tag) => (
              <li key={tag.id} className="row">
                <div className="row-body">
                  <span className="row-name">{tag.name}</span>
                  {uses(tag)}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={active.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <ul className="rows">
                {active.map((tag) => (
                  <SortableRow
                    key={tag.id}
                    id={tag.id}
                    label={tag.name}
                    editing={editingId === tag.id}
                    actions={
                      editingId === tag.id ? undefined : (
                        <>
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => {
                              setAdding(false)
                              setEditingId(tag.id)
                              setDraft(tag.name)
                              setError(null)
                            }}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            className="btn-ghost btn-danger"
                            onClick={() => handleArchive(tag)}
                          >
                            Archive
                          </button>
                        </>
                      )
                    }
                  >
                    {editingId === tag.id ? (
                      <div className="inline-edit">
                        <input
                          ref={editRef}
                          value={draft}
                          maxLength={120}
                          onChange={(e) => setDraft(e.target.value)}
                          aria-label="Tag name"
                        />
                        <div className="row-actions">
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleSaveEdit(tag.id)}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                          {/* Pushed away from Save/Cancel: it is the one
                              control here with a consequence beyond the form. */}
                          <button
                            type="button"
                            className="btn-ghost btn-danger btn-end"
                            onClick={() => handleArchive(tag)}
                          >
                            Archive
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="row-name">{tag.name}</span>
                        {uses(tag)}
                      </>
                    )}
                  </SortableRow>
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        ))}

      {canManage && !adding && !editingId && (
        <button type="button" className="btn-ghost btn-add" onClick={() => setAdding(true)}>
          + Add tag
        </button>
      )}

      {canManage && adding && (
        <form className="feature-form" onSubmit={handleAdd}>
          <input
            ref={addRef}
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New tag"
            aria-label="New tag name"
          />

          <div className="row-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Adding…' : 'Add tag'}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setAdding(false)
                setName('')
                setError(null)
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {canManage && archived.length > 0 && (
        <details className="archived">
          <summary className="muted">{archived.length} archived</summary>
          <ul className="rows">
            {archived.map((tag) => (
              <li key={tag.id} className="row is-archived">
                <div className="row-body">
                  <span className="row-name">{tag.name}</span>
                  {uses(tag)}
                </div>
                <div className="row-actions">
                  <button type="button" className="btn-ghost" onClick={() => handleRestore(tag)}>
                    Restore
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
